import axiosInstance from "./axiosInstance";

const contentReportsService = {
  /**
   * @param {number} page
   * @param {number} limit
   * @param {object} [filters]
   * @param {string} [filters.status] pending | dismissed | removed
   * @param {string} [filters.targetType] post | comment
   * @param {string|number} [filters.reportId]
   * @param {string|number} [filters.userId] reporter, post author, or comment author
   */
  async list(page = 1, limit = 20, filters = {}) {
    const f = typeof filters === "string" ? { status: filters } : filters || {};
    const params = { page, limit };
    const status = f.status;
    if (status != null && String(status).trim() !== "") {
      params.status = String(status).trim().toLowerCase();
    }
    const tt = f.targetType;
    if (tt && ["post", "comment"].includes(String(tt).trim().toLowerCase())) {
      params.targetType = String(tt).trim().toLowerCase();
    }
    const rid = Number(String(f.reportId ?? "").trim());
    if (Number.isFinite(rid) && rid > 0) params.reportId = Math.floor(rid);
    const uid = Number(String(f.userId ?? "").trim());
    if (Number.isFinite(uid) && uid > 0) params.userId = Math.floor(uid);

    const response = await axiosInstance.get("admin/users/content-reports", { params });
    return response.data;
  },

  /**
   * @param {number} id
   * @param {{ action: 'dismiss' | 'remove', adminNotes?: string }} body
   */
  async update(id, body) {
    const response = await axiosInstance.patch(`admin/users/content-reports/${id}`, body);
    return response.data;
  },

  /** Permanently delete the report row and delete linked post/comment from the app. */
  async deleteContentReport(reportId) {
    const response = await axiosInstance.delete(`admin/users/content-reports/${reportId}`);
    return response.data;
  },

  /** Ban status + counts of posts/comments not yet moderation-blocked. */
  async getCommunityModerationSummary(userId) {
    const response = await axiosInstance.get(
      `admin/users/app-user/${userId}/community-moderation-summary`
    );
    return response.data;
  },

  /** Block all posts & comments for this app user (same effect as Block content on each item). */
  async moderateAllUserContent(userId) {
    const response = await axiosInstance.post(
      `admin/users/app-user/${userId}/moderate-all-content`
    );
    return response.data;
  },

  /**
   * All pending reports whose reported post or comment was written by this user.
   * @param {'dismiss'|'remove'} action — dismiss closes reports only; remove blocks each item and closes reports.
   */
  async bulkResolveContentReports(authorUserId, { action, adminNotes }) {
    const response = await axiosInstance.post(
      `admin/users/app-user/${authorUserId}/bulk-resolve-content-reports`,
      { action, adminNotes }
    );
    return response.data;
  },

  /**
   * Clear temporary community ban. Sends email + notification to user.
   * @param {number} userId
   * @param {{ forceLift?: boolean }} [options] — forceLift skips the check that all content is blocked first.
   */
  async liftCommunityBan(userId, options = {}) {
    const response = await axiosInstance.post(`admin/users/app-user/${userId}/lift-community-ban`, {
      forceLift: options.forceLift === true,
    });
    return response.data;
  },

  /**
   * Deletes all reports tied to this user, deletes all their posts & comments, then lifts the ban.
   */
  async restoreCommunityAccess(userId) {
    const response = await axiosInstance.post(
      `admin/users/app-user/${userId}/restore-community-access`
    );
    return response.data;
  },
};

export default contentReportsService;
